import { useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { minEndIso, applyStartChangeNoFill } from '@/lib/dateRange';

// ISO `YYYY-MM-DD` ↔ Date in local time (no UTC drift).
export const isoToDate = (iso) => {
  if (!iso) return undefined;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};
export const dateToIso = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
export const formatIso = (iso) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

const TRIGGER_BASE =
  'flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-left text-[13px] shadow-sm transition-colors hover:border-ring/60 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring';

/**
 * Single-date popover field.
 * - Empty by default: no value → nothing selected, today still highlighted.
 * - `borderIso` draws a visible border on a reference day (e.g. the chosen
 *   move-in shown inside the move-out calendar).
 */
export function DateField({
  value, onChange, open, onOpenChange, placeholder = 'Select date', testId,
  disabledBefore, borderIso, defaultMonthIso, className, disabled,
}) {
  const [innerOpen, setInnerOpen] = useState(false);
  const isOpen = open !== undefined ? open : innerOpen;
  const setOpen = onOpenChange || setInnerOpen;
  const selected = isoToDate(value);
  const border = isoToDate(borderIso);

  return (
    <Popover open={isOpen} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(TRIGGER_BASE, !value && 'text-muted-foreground', className)}
          data-testid={testId}
        >
          <CalendarIcon className="h-3.5 w-3.5 opacity-60 shrink-0" />
          <span className="truncate">{value ? formatIso(value) : placeholder}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2" data-testid={testId ? `${testId}-popover` : undefined}>
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected || isoToDate(defaultMonthIso) || undefined}
          onSelect={(d) => {
            if (!d) return;
            onChange(dateToIso(d));
            setOpen(false);
          }}
          disabled={disabledBefore ? { before: isoToDate(disabledBefore) } : undefined}
          modifiers={border ? { anchor: border } : undefined}
          modifiersClassNames={{ anchor: 'border-2 border-primary text-primary font-semibold opacity-100 disabled:opacity-100' }}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Start/end pair used by every admin form (tenants, leads, parking).
 * Rules: start starts empty, picking a start auto-opens the end calendar
 * without filling it, the end calendar anchors on the start date and allows
 * start + 1 day onwards (or same day with `allowSameDay`).
 */
export function StartEndDateFields({
  start, end, onChange, allowSameDay = false,
  startLabel = 'Start Date', endLabel = 'End Date',
  startTestId, endTestId, minStartIso,
  labelClassName = 'text-[12px] text-gray-500', triggerClassName, className = 'grid grid-cols-2 gap-2',
}) {
  const [endOpen, setEndOpen] = useState(false);

  const handleStart = (iso) => {
    onChange(applyStartChangeNoFill(iso, end, { allowSameDay }));
    setTimeout(() => setEndOpen(true), 80);
  };

  return (
    <div className={className}>
      <div>
        <Label className={labelClassName}>{startLabel}</Label>
        <div className="mt-1">
          <DateField
            value={start}
            onChange={handleStart}
            placeholder="Select date"
            testId={startTestId}
            disabledBefore={minStartIso}
            className={triggerClassName}
          />
        </div>
      </div>
      <div>
        <Label className={labelClassName}>{endLabel}</Label>
        <div className="mt-1">
          <DateField
            value={end}
            onChange={(iso) => onChange({ start, end: iso })}
            open={endOpen}
            onOpenChange={setEndOpen}
            placeholder="Select date"
            testId={endTestId}
            disabledBefore={start ? minEndIso(start, allowSameDay) : minStartIso}
            borderIso={start}
            defaultMonthIso={start}
            className={triggerClassName}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Customer website: single two-month range calendar ──────────────
const RANGE_CLASSES = {
  months: 'flex flex-col sm:flex-row gap-5 sm:gap-8',
  month: 'space-y-3',
  caption: 'flex justify-center pt-1 relative items-center',
  caption_label: 'text-[15px] font-medium text-[#2F3431] tracking-tight',
  nav: 'space-x-1 flex items-center',
  nav_button: 'h-8 w-8 rounded-full bg-transparent p-0 text-[#2F3431]/60 hover:bg-[#4E6A52]/10 hover:text-[#4E6A52] transition-colors inline-flex items-center justify-center',
  nav_button_previous: 'absolute left-1',
  nav_button_next: 'absolute right-1',
  table: 'w-full border-collapse',
  head_row: 'flex',
  head_cell: 'text-[#2F3431]/50 w-9 font-medium text-[11px] uppercase tracking-wider',
  row: 'flex w-full mt-1',
  cell: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-[#4E6A52]/10 [&:has(>.day-range-start)]:rounded-l-full [&:has(>.day-range-end)]:rounded-r-full',
  day: 'h-9 w-9 p-0 font-normal rounded-full text-[#2F3431] hover:bg-[#4E6A52]/15 transition-colors inline-flex items-center justify-center',
  day_range_start: 'day-range-start bg-[#4E6A52] text-white hover:bg-[#4E6A52] hover:text-white',
  day_range_end: 'day-range-end bg-[#4E6A52] text-white hover:bg-[#4E6A52] hover:text-white',
  day_range_middle: 'rounded-none bg-transparent !text-[#2F3431]',
  day_selected: 'bg-[#4E6A52]',
  day_today: 'ring-1 ring-[#4E6A52]/40',
  day_outside: 'text-[#2F3431]/30',
  day_disabled: 'text-[#2F3431]/25 line-through cursor-not-allowed hover:bg-transparent',
  day_hidden: 'invisible',
};

/**
 * Two-month side-by-side range calendar for the public site.
 * First click sets move-in, second click sets move-out; clicking a day on or
 * before the current move-in restarts the selection from that day.
 */
export function RangeDatePicker({
  start, end, onChange, minIso, testId = 'date-range', triggerClassName, align = 'start',
}) {
  const [open, setOpen] = useState(false);
  const [months, setMonths] = useState(2);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const sync = () => setMonths(mq.matches ? 2 : 1);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const from = isoToDate(start);
  const to = isoToDate(end);

  const handleDayClick = (day, modifiers) => {
    if (modifiers?.disabled) return;
    const iso = dateToIso(day);
    if (!start || end || iso <= start) {
      onChange({ start: iso, end: '' });
      return;
    }
    onChange({ start, end: iso });
    setTimeout(() => setOpen(false), 150);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'w-full rounded-2xl border border-[#D7E0D5] bg-white px-4 py-2.5 text-left text-[15px] transition-all hover:border-[#4E6A52]/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4E6A52]/30',
            triggerClassName
          )}
          data-testid={testId}
        >
          <span className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-[#4E6A52] shrink-0" />
            <span className={cn('truncate', !start && 'text-[#2F3431]/50')}>
              {start ? `${formatIso(start)} — ${end ? formatIso(end) : 'Select move-out'}` : 'Select your dates'}
            </span>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className="w-auto max-w-[calc(100vw-2rem)] p-4 rounded-2xl border-[#D7E0D5] shadow-[0_10px_40px_rgba(16,24,40,0.12)] bg-white"
        data-testid={`${testId}-popover`}
      >
        <Calendar
          mode="range"
          numberOfMonths={months}
          selected={{ from, to }}
          defaultMonth={from || undefined}
          onDayClick={handleDayClick}
          disabled={minIso ? { before: isoToDate(minIso) } : undefined}
          classNames={RANGE_CLASSES}
          className="p-0"
        />
        <p className="mt-2 text-[12px] text-[#2F3431]/55">
          {start && !end ? 'Now pick your move-out date (minimum one night).' : 'Pick your move-in, then your move-out.'}
        </p>
      </PopoverContent>
    </Popover>
  );
}
